import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatCLP } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  Star, Clock, MonitorPlay, CheckCircle, ShoppingCart, User, ListChecks,
  BookOpen, PlayCircle, FileText, CircleNotch, GraduationCap,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const modalityLabels = { online: "Online", presencial: "Presencial", mixta: "Mixta" };

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useCart();
  const [course, setCourse] = useState(null);
  const [adding, setAdding] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => navigate("/catalogo"));
    if (user && user.role === "student") {
      api.get(`/enrollments/${id}`).then(() => setEnrolled(true)).catch(() => setEnrolled(false));
    }
  }, [id, user]);

  const addToCart = async () => {
    if (!user) { navigate("/login", { state: { from: `/curso/${id}` } }); return; }
    if (user.role !== "student") { toast.error("Solo los alumnos pueden comprar cursos."); return; }
    setAdding(true);
    try { await cart.add(id); toast.success("Agregado al carrito"); }
    catch { toast.error("No se pudo agregar"); }
    finally { setAdding(false); }
  };

  if (!course) return <div className="min-h-[60vh] flex items-center justify-center"><CircleNotch size={32} className="animate-spin text-primary" /></div>;

  const totalLessons = (course.modules || []).reduce((a, m) => a + (m.lessons?.length || 0), 0);

  return (
    <div>
      <div className="bg-[#1E293B] text-white">
        <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Badge className="bg-primary text-white hover:bg-primary">{course.category}</Badge>
              <Badge variant="outline" className="border-white/30 text-slate-200 gap-1">
                <MonitorPlay size={13} /> {modalityLabels[course.modality]}
              </Badge>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-800 tracking-tight leading-tight">{course.title}</h1>
            <p className="text-slate-300 mt-4 leading-relaxed max-w-2xl">{course.description}</p>
            <div className="flex flex-wrap items-center gap-6 mt-6 text-sm">
              <span className="flex items-center gap-1.5"><Star size={17} weight="fill" className="text-accent" /> {course.rating} valoración</span>
              <span className="flex items-center gap-1.5"><Clock size={17} /> {course.duration_hours} horas</span>
              <span className="flex items-center gap-1.5"><ListChecks size={17} /> {totalLessons} lecciones</span>
              <span className="flex items-center gap-1.5"><User size={17} /> {course.instructor_name}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {course.objectives?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl font-800 tracking-tight mb-4">Objetivos de aprendizaje</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {course.objectives.map((o, i) => (
                  <div key={i} className="flex gap-3 items-start bg-white border border-border rounded-lg p-4">
                    <CheckCircle size={20} weight="fill" className="text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{o}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="font-display text-2xl font-800 tracking-tight mb-4">Temario del curso</h2>
            <Accordion type="single" collapsible className="bg-white border border-border rounded-lg px-4">
              {(course.modules || []).map((m, i) => (
                <AccordionItem key={m.id} value={m.id} className="border-border">
                  <AccordionTrigger className="hover:no-underline" data-testid={`module-${i}`}>
                    <div className="flex items-center gap-3 text-left">
                      <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0">
                        <BookOpen size={16} className="text-primary" />
                      </div>
                      <span className="font-semibold">{m.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pl-11">
                      {(m.lessons || []).map((l) => (
                        <li key={l.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                          {l.type === "video" ? <PlayCircle size={16} /> : <FileText size={16} />}
                          {l.title} <span className="text-xs">· {l.duration_min} min</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {course.syllabus?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl font-800 tracking-tight mb-4">Contenidos</h2>
              <ul className="grid sm:grid-cols-2 gap-2">
                {course.syllabus.map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> {s}</li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Purchase card */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-border rounded-lg overflow-hidden sticky top-24">
            <div className="aspect-video bg-secondary overflow-hidden">
              <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="font-display text-3xl font-900 tracking-tight">{formatCLP(course.price)}</div>
              <p className="text-sm text-muted-foreground mt-1">Acceso completo · Certificado al aprobar</p>

              {enrolled ? (
                <Button onClick={() => navigate(`/dashboard/curso/${id}`)} data-testid="course-continue-btn"
                  className="w-full mt-5 bg-primary hover:bg-blue-600 text-white gap-2">
                  <PlayCircle size={18} weight="fill" /> Continuar curso
                </Button>
              ) : (
                <Button onClick={addToCart} disabled={adding} data-testid="course-add-cart-btn"
                  className="w-full mt-5 bg-primary hover:bg-blue-600 text-white gap-2">
                  {adding ? <CircleNotch className="animate-spin" /> : <><ShoppingCart size={18} /> Agregar al carrito</>}
                </Button>
              )}

              <ul className="mt-6 space-y-3 text-sm">
                {[
                  [Clock, `${course.duration_hours} horas de contenido`],
                  [GraduationCap, `Nota mínima ${course.passing_grade} · Asistencia ${course.min_attendance}%`],
                  [ListChecks, "Evaluación final tipo quiz"],
                  [CheckCircle, "Certificado PDF con folio verificable"],
                ].map(([Icon, t], i) => (
                  <li key={i} className="flex items-center gap-3 text-muted-foreground">
                    <Icon size={18} className="text-primary shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
